import { useEffect, useRef, useCallback } from 'react';

/**
 * Tracks user inactivity (mousedown, keydown, scroll, touchstart) and fires warning + timeout callbacks.
 * Activity in any tab resets all tabs' timers via BroadcastChannel + localStorage.
 *
 * @param {Object} params
 * @param {number}   params.timeoutMinutes      0 = disabled; > 0 = minutes until logout
 * @param {Function} params.onTimeout           Called when the timer expires
 * @param {Function} params.onWarning           Called with (secondsRemaining) when warning fires
 * @param {Function} params.onReset             Called when activity resets the timer (use to dismiss warning dialog)
 * @param {number}   params.warningLeadSeconds  Seconds before timeout to fire onWarning
 */
export function useInactivityTimer({
  timeoutMinutes,
  onTimeout,
  onWarning,
  onReset,
  warningLeadSeconds = 60,
}) {
  const timeoutRef   = useRef(null);
  const warningRef   = useRef(null);
  const onTimeoutRef = useRef(onTimeout);
  const onWarningRef = useRef(onWarning);
  const onResetRef   = useRef(onReset);

  // Keep callback refs current without re-running the main effect
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);
  useEffect(() => { onWarningRef.current = onWarning; }, [onWarning]);
  useEffect(() => { onResetRef.current   = onReset;   }, [onReset]);

  const clearTimers = useCallback(() => {
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    timeoutRef.current = null;
    warningRef.current = null;
  }, []);

  const resetTimer = useCallback(() => {
    if (!timeoutMinutes || timeoutMinutes <= 0) return;
    clearTimers();

    const totalMs   = timeoutMinutes * 60 * 1000;
    const warnDelay = Math.max(totalMs - warningLeadSeconds * 1000, 0);

    if (warnDelay > 0) {
      warningRef.current = setTimeout(
        () => onWarningRef.current?.(warningLeadSeconds),
        warnDelay
      );
    }

    timeoutRef.current = setTimeout(() => onTimeoutRef.current?.(), totalMs);
  }, [timeoutMinutes, warningLeadSeconds, clearTimers]);

  useEffect(() => {
    if (!timeoutMinutes || timeoutMinutes <= 0) return;

    const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    // Listen for activity broadcast from other tabs
    const onInboundMessage = (event) => {
      if (event?.data?.type === 'ACTIVITY') {
        resetTimer();
        onResetRef.current?.();
      }
    };
    const onStorage = (e) => {
      if (e.key === 'apix:activity') {
        resetTimer();
        onResetRef.current?.();
      }
    };

    // Persistent BC instance — used for both receiving and sending activity broadcasts.
    // Using a persistent instance for sending is more reliable than create-post-close.
    // BroadcastChannel does not deliver messages back to the sender's own instance.
    let inboundBc = null;
    try {
      if ('BroadcastChannel' in window) {
        inboundBc = new BroadcastChannel('apix-auth');
        inboundBc.onmessage = onInboundMessage;
      }
    } catch (_) {}

    // Broadcast activity to other tabs — separate try-catches so localStorage
    // always runs even if the BC call throws.
    const broadcastActivity = () => {
      try { inboundBc?.postMessage({ type: 'ACTIVITY', ts: Date.now() }); } catch (_) {}
      try { window.localStorage?.setItem('apix:activity', String(Date.now())); } catch (_) {}
    };

    // 1s debounce: reset own timer + broadcast to other tabs + dismiss any warning dialog
    let debounceHandle = null;
    const handleActivity = () => {
      if (debounceHandle) return;
      debounceHandle = setTimeout(() => {
        debounceHandle = null;
        resetTimer();
        broadcastActivity();
        onResetRef.current?.();
      }, 1000);
    };

    ACTIVITY_EVENTS.forEach(e =>
      window.addEventListener(e, handleActivity, { passive: true })
    );
    window.addEventListener('storage', onStorage);

    resetTimer();

    return () => {
      clearTimers();
      clearTimeout(debounceHandle);
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, handleActivity));
      window.removeEventListener('storage', onStorage);
      if (inboundBc) inboundBc.close();
    };
  }, [timeoutMinutes, resetTimer, clearTimers]);

  return { resetTimer };
}
